import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI } from 'sonata-react-components';

import './csvDropzone.scss';

type Props = {
  className?: string;
  onFile: (file: File) => void;
  templateUrl?: string;
  templateLabel?: string;
  uploadLabel?: string;
  warningLabel?: string;
  inputId?: string;
};

export function CsvDropzone({
  className,
  onFile,
  templateUrl,
  templateLabel,
  uploadLabel,
  warningLabel,
  inputId = 'csv-dropzone-input'
}: Props) {
  const { formatMessage } = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file?.name.endsWith('.csv')) {
      onFile(file);
    }
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    event.preventDefault();
    const file = event.currentTarget.files?.[0];
    if (file) {
      onFile(file);
    }
  }

  return (
    <div className={`csvDropzone${className ? `${className}` : ''}`} onDragOver={handleDragOver} onDrop={handleDrop}>
      <div className="csvDropzone__content">
        <span className="csvDropzone__contentIcon">
          <i className="fa-kit fa-regular-folder-circle-plus" />
        </span>
        <p className="csvDropzone__contentText">
          {formatMessage({
            defaultMessage: 'Drag and drop your ".CSV" file to start the upload',
            id: 'attributionPOI.modal.dropzone.text'
          })}
        </p>
        <span className="csvDropzone__contentSeparator">
          {formatMessage({
            defaultMessage: 'Or',
            id: 'common.or'
          })}
        </span>
        {templateUrl && (
          <ButtonUI
            icon="fa-solid fa-arrow-down-to-line"
            id="csvDropzone-templateDownload"
            onClick={() => window.open(templateUrl)}
            shape="link"
            size="small"
            text={
              templateLabel ??
              formatMessage({
                defaultMessage: 'CSV Template',
                id: 'csvDropzone.templateDownload'
              })
            }
            type="secondary"
          />
        )}
        <label className="csvDropzone__button" htmlFor={inputId}>
          <ButtonUI
            icon="fas fa-arrow-to-top"
            iconPosition="left"
            shape="squareRounded"
            size="medium"
            text={
              uploadLabel ??
              formatMessage({
                defaultMessage: 'Upload data file',
                id: 'attributionPOI.modal.dropzone.uploadButton'
              })
            }
            type="primary"
          />
          <input
            accept=".csv"
            className="csvDropzone__fileInput"
            hidden
            id={inputId}
            name={inputId}
            onChange={handleFileChange}
            ref={inputRef}
            type="file"
          />
        </label>
        {warningLabel && <div className="csvDropzone__warning">{warningLabel}</div>}
      </div>
    </div>
  );
}
